import { useQuery } from "@tanstack/react-query";
import { format as formatDate } from "date-fns";
import { ownerReportsContract } from "@common/contracts/owner-reports.contract";
import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    REPORTS_CONTROLLER_DATE_FORMAT,
} from "@common/constants";
import { ReportFilters } from "@common/types/filters/reports";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const cashFlowApiClient = getBackendAPIClient(ownerReportsContract);
const cashFlowBlobClient = getBackendAPIClient(ownerReportsContract, "blob");

function getCycle(cycle: AccountingReportCycleOptionType) {
    if (
        cycle === AccountingReportCycle.MONTH ||
        cycle === AccountingReportCycle.WEEK ||
        cycle === AccountingReportCycle.YEAR
    ) {
        return cycle;
    }

    return AccountingReportCycle.MONTH;
}

async function getCashFlowTabReport(
    query: Parameters<typeof cashFlowApiClient.getCashFlowReport>[0]["query"],
) {
    const response = await cashFlowApiClient.getCashFlowReport({ query });
    return response.body;
}

export async function getProfitLossTabTopTransactions(
    query: Parameters<typeof cashFlowApiClient.getTopTransactions>[0]["query"],
) {
    const response = await cashFlowApiClient.getTopTransactions({ query });
    return response.body;
}

function buildCashFlowQuery(filters: ReportFilters) {
    return {
        startDate: filters.start
            ? formatDate(filters.start, REPORTS_CONTROLLER_DATE_FORMAT)
            : undefined,
        endDate: filters.end
            ? formatDate(filters.end, REPORTS_CONTROLLER_DATE_FORMAT)
            : undefined,
        cycle: getCycle(filters.cycle),
        entityIds: filters.entityIds,
        accountIds: filters.accountIds,
        category: filters.category,
        classes: filters.classes,
        classFilterType: filters.classFilterType,
    };
}

interface ExportCashFlowReportParams {
    filters: ReportFilters;
}

export async function exportCashFlowReport({
    filters,
}: ExportCashFlowReportParams) {
    return await cashFlowBlobClient.exportCashFlowReport({
        query: buildCashFlowQuery(filters),
    });
}

interface CashFlowQueryOptions {
    filters: ReportFilters;
    refetchOnWindowFocus?: boolean;
}

export function useCashFlowQuery({
    filters,
    refetchOnWindowFocus = false,
}: CashFlowQueryOptions) {
    return useQuery({
        queryKey: ["cashFlowTabReport", filters],
        queryFn: async () => {
            const filtersUsed = structuredClone(filters);

            return {
                report: await getCashFlowTabReport(buildCashFlowQuery(filters)),
                filtersUsed,
                reportCreatedAt: new Date().getTime(),
            };
        },
        enabled: filters.entityIds?.length > 0,
        refetchOnWindowFocus,
    });
}
